// BOORO: the dashboard. The numbers of a period: what the team finished, how
// often it worked, how much time it saved and what it cost.
import type { TaskMetrics, TaskStats } from "@rakazo/contracts";

/** A period the dashboard shows, and the period before it for comparison. */
export type DashboardPeriod = "7d" | "30d" | "90d" | "month";

/** How long a period covers, in days. A month follows the calendar. */
export const PERIOD_DAYS: Record<Exclude<DashboardPeriod, "month">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

/** The start of a period, ending now. */
export function periodStart(period: DashboardPeriod, now: Date): Date {
  if (period === "month") {
    const start = new Date(now);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return start;
  }
  const start = new Date(now);
  start.setDate(start.getDate() - PERIOD_DAYS[period]);
  return start;
}

/** The period before the current one, of the same length. */
export function previousPeriodStart(period: DashboardPeriod, now: Date): Date {
  if (period === "month") {
    const start = new Date(now);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    start.setMonth(start.getMonth() - 1);
    return start;
  }
  const start = periodStart(period, now);
  start.setDate(start.getDate() - PERIOD_DAYS[period]);
  return start;
}

/** A day or a week, as the dashboard groups the tasks. */
export type SeriesBucket = "day" | "week";

/**
 * The start of the bucket a moment belongs to, in the given time zone. The
 * week starts on Monday, as it does here. The result is the local time of
 * that day, so the buckets line up with the days the person sees.
 */
export function bucketStart(moment: Date, bucket: SeriesBucket, timeZone: string): Date {
  // The parts of the moment in the person's own time zone.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(moment);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? "0");
  const year = read("year");
  const month = read("month");
  const day = read("day");
  const hour = read("hour") % 24;
  const minute = read("minute");
  const second = read("second");

  // The local date, as a plain calendar date.
  const local = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  if (bucket === "week") {
    const weekday = local.getUTCDay();
    const sinceMonday = (weekday + 6) % 7;
    local.setUTCDate(local.getUTCDate() - sinceMonday);
  }
  local.setUTCHours(0, 0, 0, 0);
  return local;
}

/** The days a period covers, oldest first. */
export function periodDays(period: DashboardPeriod, now: Date): Date[] {
  const start = periodStart(period, now);
  const end = new Date(now);
  const days: Date[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/** The tasks that count towards the success rate. */
export function finishedTaskCount(stats: TaskStats): number {
  return stats.total.success + stats.total.reworked + stats.total.failed;
}

/**
 * The change against the period before, as a factor. Null when there is
 * nothing to compare with, so the view can say "no change yet" instead of
 * a made-up number.
 */
export function periodChange(
  current: number | null,
  previous: number | null,
): { direction: "up" | "down" | "flat"; percent: number | null } {
  if (current === null || previous === null) return { direction: "flat", percent: null };
  if (previous === 0) {
    return { direction: current > 0 ? "up" : "flat", percent: null };
  }
  const change = (current - previous) / previous;
  if (Math.abs(change) < 0.005) return { direction: "flat", percent: 0 };
  return { direction: change > 0 ? "up" : "down", percent: Math.round(Math.abs(change) * 100) };
}

/** The saved minutes of a task, counting only what came out. */
export function savedMinutes(outcome: TaskMetrics["outcome"], minutesSaved: number | null): number {
  if (minutesSaved === null) return 0;
  return outcome === "success" || outcome === "reworked" ? minutesSaved : 0;
}

/** The minutes a person would have needed, in a readable form. */
export function formatSavedMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded} h`;
}

/** The cost in a readable form; null when no run reported one. */
export function formatCost(costUsd: number | null, locale = "de-DE"): string {
  if (costUsd === null) return "—";
  const money = (value: number) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "USD" }).format(value);
  if (costUsd > 0 && costUsd < 0.01) return `< ${money(0.01)}`;
  return money(costUsd);
}

/** The success rate in a readable form; null before a task was finished. */
export function formatSuccessRate(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 100)} %`;
}
